import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2, Save, Send, CheckCircle2, AlertCircle, ExternalLink, FileSignature, Inbox, ArrowUpRight } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SensitiveOtpGate from "@/components/SensitiveOtpGate";
import { AgencyInboxSettingsSection } from "@/components/AgencyInboxSettingsSection";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "image"],
    ["clean"],
  ],
};
const QUILL_FORMATS = ["header", "bold", "italic", "underline", "strike", "color", "background", "list", "align", "link", "image"];

export default function EmailSettings() {
  return (
    <SensitiveOtpGate
      title="Verifica accesso impostazioni Email"
      description="Per modificare la configurazione SMTP dell'agenzia, conferma la tua identità con un codice inviato all'email dell'amministratore."
    >
      <EmailSettingsForm />
    </SensitiveOtpGate>
  );
}

function EmailSettingsForm() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [appPassword, setAppPassword] = useState("");
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [activeTab, setActiveTab] = useState("smtp"); // 🆕 "smtp" | "imap"

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/agency/email-settings"); setSettings(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      const payload = {
        smtp_enabled: !!settings.smtp_enabled,
        smtp_provider_type: settings.smtp_provider_type || "gmail",
        smtp_email: settings.smtp_email || null,
        smtp_username: settings.smtp_username || null,
        smtp_display_name: settings.smtp_display_name || null,
        smtp_host: settings.smtp_host || null,
        smtp_port: settings.smtp_port ? Number(settings.smtp_port) : null,
        email_signature_html: settings.email_signature_html || null,
      };
      if (passwordChanged && appPassword) payload.smtp_app_password = appPassword;
      const { data } = await api.put("/agency/email-settings", payload);
      setSettings(data);
      setAppPassword(""); setPasswordChanged(false);
      toast.success("Impostazioni email salvate");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const sendTest = async (targetOverride = null) => {
    setBusy(true);
    try {
      const payload = targetOverride ? { target: targetOverride } : {};
      const { data } = await api.post("/agency/email-settings/test", payload);
      toast.success(`Email di prova inviata a ${data.sent_to}`);
      await load(); // 🆕 ricarica per attivare il badge "SMTP verificato ✅"
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio test"); }
    finally { setBusy(false); }
  };
  const sendTestToSelf = () => sendTest(settings?.smtp_email || null);

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="email-settings-page">
      <header>
        <p className="text-xs uppercase tracking-wider text-slate-500">Configurazione</p>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Mail className="h-6 w-6 text-blue-600" /> Impostazioni Email
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Configura l&apos;email da cui partiranno tutte le comunicazioni della tua agenzia (preventivi, polizze, promemoria).
        </p>
      </header>

      {/* 🆕 Barra tabs colorati: SMTP (verde) / IMAP (blu) */}
      <div className="flex gap-2 border-b border-slate-200" data-testid="email-settings-tabs">
        <button
          type="button"
          data-testid="tab-smtp"
          onClick={() => setActiveTab("smtp")}
          className={`flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 font-semibold text-sm transition ${
            activeTab === "smtp"
              ? "border-emerald-600 text-emerald-700 bg-emerald-50/50"
              : "border-transparent text-slate-500 hover:text-emerald-700 hover:bg-emerald-50/30"
          }`}
        >
          <ArrowUpRight className="h-4 w-4" /> Invio (SMTP)
          {activeTab === "smtp" && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />}
        </button>
        <button
          type="button"
          data-testid="tab-imap"
          onClick={() => setActiveTab("imap")}
          className={`flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 font-semibold text-sm transition ${
            activeTab === "imap"
              ? "border-blue-600 text-blue-700 bg-blue-50/50"
              : "border-transparent text-slate-500 hover:text-blue-700 hover:bg-blue-50/30"
          }`}
        >
          <Inbox className="h-4 w-4" /> Ricezione (IMAP)
          {activeTab === "imap" && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-blue-500" />}
        </button>
      </div>

      {/* 🆕 Tab content — SMTP (Invio) */}
      {activeTab === "smtp" && (<>

      <Card className="border-amber-300 bg-amber-50/30">
        <CardContent className="py-3 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <p className="font-semibold">Provider supportati (Gmail, Yahoo, Outlook, custom):</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li><strong>Gmail:</strong> 2FA + <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-0.5">App Password <ExternalLink className="h-3 w-3" /></a> · Host: <code>smtp.gmail.com</code> · Porta: <code>465</code> (SSL)</li>
                <li><strong>Yahoo:</strong> 2FA + <a href="https://login.yahoo.com/account/security/app-passwords" target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-0.5">Password per l&apos;app <ExternalLink className="h-3 w-3" /></a> · Host: <code>smtp.mail.yahoo.com</code> · Porta: <code>465</code> (SSL) o <code>587</code></li>
                <li><strong>Outlook/Office365:</strong> Host: <code>smtp.office365.com</code> · Porta: <code>587</code></li>
              </ul>
              <p className="text-[11px]">La password viene <strong>cifrata a riposo</strong> (Fernet AES-128) prima di essere salvata nel database. Non è la password normale della tua email: usa sempre una App Password.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">Credenziali SMTP</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid="email-enabled"
              checked={!!settings.smtp_enabled}
              onChange={(e) => setSettings({ ...settings, smtp_enabled: e.target.checked })}
              className="h-4 w-4"
            />
            <span>Usa le mie credenziali (altrimenti viene usato lo <strong>SMTP di default della piattaforma</strong> configurato dal super-admin)</span>
          </label>

          {/* 🆕 Radio: Tipologia invio email — Gmail vs SMTP generico */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Tipologia invio email</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <label
                data-testid="provider-type-gmail"
                className={`flex items-start gap-2 p-3 rounded border cursor-pointer transition ${
                  (settings.smtp_provider_type || "gmail") === "gmail"
                    ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="smtp_provider_type"
                  value="gmail"
                  className="mt-1"
                  checked={(settings.smtp_provider_type || "gmail") === "gmail"}
                  onChange={() => setSettings({ ...settings, smtp_provider_type: "gmail", smtp_host: "", smtp_port: null, smtp_username: "" })}
                />
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-slate-800">📮 Google (Gmail)</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Semplice: solo email + App Password. Host <code>smtp.gmail.com</code>, porta <code>465</code> (SSL) forzati. Il mittente viene sempre allineato allo username Gmail per evitare blocchi.</div>
                </div>
              </label>
              <label
                data-testid="provider-type-smtp"
                className={`flex items-start gap-2 p-3 rounded border cursor-pointer transition ${
                  settings.smtp_provider_type === "smtp"
                    ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="smtp_provider_type"
                  value="smtp"
                  className="mt-1"
                  checked={settings.smtp_provider_type === "smtp"}
                  onChange={() => setSettings({ ...settings, smtp_provider_type: "smtp" })}
                />
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-slate-800">🔧 SMTP generico</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Yahoo, Outlook, Aruba, Libero, dominio custom. Puoi impostare host, porta e username separati.</div>
                </div>
              </label>
            </div>
          </div>

          <div>
            <Label>📧 Email mittente <span className="text-red-500">*</span></Label>
            <Input
              data-testid="email-address"
              type="email"
              value={settings.smtp_email || ""}
              onChange={(e) => setSettings({ ...settings, smtp_email: e.target.value.toLowerCase() })}
              placeholder="nome@tuaagenzia.it"
            />
            <p className="text-[10px] text-slate-400 mt-0.5">Indirizzo visibile nel campo &quot;Da:&quot; dei destinatari (clienti, broker).</p>
          </div>

          {/* Campi visibili SOLO per SMTP generico */}
          {settings.smtp_provider_type === "smtp" && (
            <>
              {/* Provider preset picker (comodità) */}
              <div>
                <Label>🌐 Preset provider (imposta host + porta)</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {[
                    { key: "yahoo", label: "Yahoo", host: "smtp.mail.yahoo.com", port: 465 },
                    { key: "outlook", label: "Outlook / 365", host: "smtp.office365.com", port: 587 },
                    { key: "libero", label: "Libero", host: "smtp.libero.it", port: 465 },
                    { key: "aruba", label: "Aruba", host: "smtps.aruba.it", port: 465 },
                    { key: "custom", label: "Personalizzato", host: "", port: "" },
                  ].map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      data-testid={`smtp-preset-${p.key}`}
                      onClick={() => setSettings({ ...settings, smtp_host: p.host, smtp_port: p.port })}
                      className={`text-xs px-2.5 py-1 rounded border transition ${
                        (settings.smtp_host || "") === p.host && p.host
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label>Server SMTP (host) <span className="text-red-500">*</span></Label>
                  <Input
                    data-testid="smtp-host"
                    value={settings.smtp_host || ""}
                    onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value.trim().toLowerCase() })}
                    placeholder="smtp.mail.yahoo.com"
                  />
                </div>
                <div>
                  <Label>Porta <span className="text-red-500">*</span></Label>
                  <Input
                    data-testid="smtp-port"
                    type="number"
                    min={1}
                    max={65535}
                    value={settings.smtp_port || ""}
                    onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
                    placeholder="465"
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">465 = SSL · 587 = STARTTLS</p>
                </div>
              </div>
              <div>
                <Label>Username auth <span className="text-[10px] text-slate-400 font-normal">(facoltativo — se diverso dall&apos;email mittente)</span></Label>
                <Input
                  data-testid="smtp-username"
                  value={settings.smtp_username || ""}
                  onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                  placeholder="Lascia vuoto per usare l'email mittente come username"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">Alcuni provider (es. Aruba) richiedono un username diverso dall&apos;indirizzo email.</p>
              </div>
            </>
          )}

          <div>
            <Label>🔐 Password SMTP {settings.smtp_app_password_set && <span className="text-[10px] text-emerald-700 font-normal">— salvata, lascia vuoto per non modificare</span>}</Label>
            <Input
              data-testid="email-password"
              type="text"
              value={appPassword}
              onChange={(e) => { setAppPassword(e.target.value); setPasswordChanged(true); }}
              placeholder={settings.smtp_app_password_set ? "**** **** **** ****" : ((settings.smtp_provider_type || "gmail") === "gmail" ? "App Password Gmail (16 caratteri)" : "Password SMTP")}
              className="font-mono"
            />
            <p className="text-[10px] text-slate-400 mt-0.5">{(settings.smtp_provider_type || "gmail") === "gmail" ? "Non è la password Gmail normale: crea una App Password dedicata. Gli spazi vengono rimossi automaticamente." : "Password dell'account SMTP. Memorizzata cifrata (Fernet AES-128)."}</p>
          </div>
          <div>
            <Label>Nome visualizzato (facoltativo)</Label>
            <Input
              data-testid="email-display-name"
              value={settings.smtp_display_name || ""}
              onChange={(e) => setSettings({ ...settings, smtp_display_name: e.target.value })}
              placeholder="Es. Agenzia Rossi Assicurazioni"
            />
            <p className="text-[10px] text-slate-400 mt-0.5">Come comparirà il mittente nei client di posta dei clienti.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-200">
            <Button
              data-testid="email-save"
              onClick={save}
              disabled={busy}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva impostazioni
            </Button>
            <Button
              data-testid="email-test"
              variant="outline"
              onClick={() => sendTest()}
              disabled={busy || !settings.smtp_enabled || !settings.smtp_email || !settings.smtp_app_password_set}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Invia email di test (a me)
            </Button>
            {/* 🆕 Pulsante self-check: invia all'email dell'agenzia stessa */}
            <Button
              data-testid="email-test-self"
              variant="outline"
              onClick={sendTestToSelf}
              disabled={busy || !settings.smtp_enabled || !settings.smtp_email || !settings.smtp_app_password_set}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              title={`Invia una email di prova a ${settings.smtp_email || "…"} per verificare la casella dell'agenzia`}
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Invia Email di Prova {settings.smtp_email && <span className="ml-1 hidden md:inline text-[10px] font-mono">→ {settings.smtp_email}</span>}
            </Button>
            {settings.smtp_enabled && settings.smtp_app_password_set && (
              settings.smtp_verified ? (
                <span
                  className="text-xs text-emerald-800 flex items-center gap-1 ml-2 px-2 py-1 rounded bg-emerald-100 border border-emerald-300 font-semibold"
                  data-testid="smtp-verified-badge"
                  title={settings.smtp_verified_at ? `Ultimo test riuscito: ${new Date(settings.smtp_verified_at).toLocaleString("it-IT")}` : ""}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> SMTP verificato ✅
                  {settings.smtp_verified_at && (
                    <span className="font-normal text-emerald-700 text-[10px] ml-1">
                      · {new Date(settings.smtp_verified_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-xs text-amber-700 flex items-center gap-1 ml-2 px-2 py-1 rounded bg-amber-50 border border-amber-300" data-testid="smtp-unverified-badge">
                  <AlertCircle className="h-3.5 w-3.5" /> SMTP configurato — non ancora testato
                </span>
              )
            )}
          </div>
        </CardContent>
      </Card>

      {/* Firma email automatica */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-violet-600" /> Firma email automatica
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Verrà appesa automaticamente in fondo a tutte le email outbound (preventivi, offerte, promemoria revisione/bollo).
            Supporta HTML (link, grassetto, immagini base64, ecc).
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <details className="rounded-md border border-blue-200 bg-blue-50/60 p-2">
            <summary className="text-xs font-semibold text-blue-900 cursor-pointer flex items-center gap-1">
              ✨ Variabili dinamiche disponibili (clicca per espandere)
            </summary>
            <p className="text-[11px] text-blue-800 mt-2 mb-2">
              Inserisci queste etichette nella firma: saranno sostituite con i dati reali al momento dell&apos;invio.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
              {[
                { v: "agency_name", l: "Nome agenzia" },
                { v: "agency_phone", l: "Telefono agenzia" },
                { v: "agency_email", l: "Email agenzia" },
                { v: "agency_full_address", l: "Indirizzo completo" },
                { v: "agency_city", l: "Città agenzia" },
                { v: "agency_pec", l: "PEC agenzia" },
                { v: "agency_piva", l: "P.IVA agenzia" },
                { v: "agency_website", l: "Sito web" },
                { v: "user_name", l: "Nome operatore" },
                { v: "user_email", l: "Email operatore" },
                { v: "user_phone", l: "Tel. operatore" },
                { v: "today", l: "Data oggi" },
              ].map((vr) => (
                <button
                  key={vr.v}
                  type="button"
                  data-testid={`var-copy-${vr.v}`}
                  onClick={() => {
                    const tag = `{{${vr.v}}}`;
                    try { Promise.resolve(navigator.clipboard?.writeText(tag)).catch(() => {}); } catch { /* noop */ }
                    toast.success(`${tag} copiato — incollalo nella firma`);
                  }}
                  className="text-left text-[11px] bg-white border border-blue-200 rounded px-2 py-1 hover:bg-blue-100 transition"
                  title={`Clicca per copiare {{${vr.v}}}`}
                >
                  <code className="text-blue-700 font-mono font-bold">{`{{${vr.v}}}`}</code>
                  <span className="block text-[10px] text-slate-500">{vr.l}</span>
                </button>
              ))}
            </div>
          </details>
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">
              {showHtmlSource ? "Modalità HTML (avanzata)" : "Editor visuale (consigliato)"}
            </p>
            <button
              type="button"
              data-testid="email-signature-toggle-mode"
              onClick={() => setShowHtmlSource((v) => !v)}
              className="text-[11px] text-violet-600 hover:underline"
            >
              {showHtmlSource ? "Torna all'editor visuale" : "Modifica HTML"}
            </button>
          </div>
          {showHtmlSource ? (
            <Textarea
              data-testid="email-signature"
              rows={8}
              value={settings.email_signature_html || ""}
              onChange={(e) => setSettings({ ...settings, email_signature_html: e.target.value })}
              placeholder={"<strong>Agenzia Rossi</strong><br/>Via Roma 10, Milano<br/>..."}
              className="font-mono text-xs"
            />
          ) : (
            <div className="border border-slate-200 rounded-md bg-white" data-testid="email-signature-quill">
              <ReactQuill
                theme="snow"
                value={settings.email_signature_html || ""}
                onChange={(html) => setSettings({ ...settings, email_signature_html: html })}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Scrivi la firma — usa la toolbar in alto per formattare (grassetto, link, lista, ecc.)"
              />
            </div>
          )}
          {settings.email_signature_html && (
            <div className="border border-slate-200 rounded-md p-3 bg-white">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Anteprima firma (come la vedrà il cliente)</p>
              <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "16px 0" }} />
              <div
                style={{ fontSize: 11, color: "#475569", lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: settings.email_signature_html }}
              />
            </div>
          )}
          <Button
            data-testid="email-signature-save"
            onClick={save}
            disabled={busy}
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva firma
          </Button>
        </CardContent>
      </Card>

      </>)}

      {/* 🆕 Tab content — IMAP (Ricezione) */}
      {activeTab === "imap" && (
        <Card className="border-blue-200 bg-blue-50/20">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-blue-800">
              <Inbox className="h-5 w-5 text-blue-600" /> Casella IMAP Agenzia
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Configura la ricezione email di questa agenzia (indipendente dalla casella globale della piattaforma).
              Le risposte dei clienti verranno automaticamente collegate a preventivi, polizze e sinistri.
            </p>
          </CardHeader>
          <CardContent>
            <AgencyInboxSettingsSection variant="flat" />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
